import { useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Plus, Save, Trash2, Undo2 } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody, CardHeader } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { Skeleton, SkeletonRegion } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { formatarDataHora } from '@/lib/format';
import { useRascunho } from '@/lib/rascunho';
import { Modal } from '@/features/tickets/Modal';
import { NomeDePessoa, useDicionarioDePessoas } from '@/features/tickets/pessoas';
import { chaves, listarChamados } from '@/features/tickets/tickets.api';
import { AvisoDeFalha, AvisoDeRegistroMudado } from '@/features/workflow/componentes';
import { classificarFalha, type Falha } from '@/features/workflow/recusa';
import { rotuloDeHorario } from '@/features/changes/calendario';
import { chavesDeMudanca, obterMudanca } from '@/features/changes/changes.api';
import {
  Campo,
  CLASSE_DE_CAMPO,
  Dado,
  EtiquetaDeDesfecho,
  EtiquetaDeJanela,
  EtiquetaDeRisco,
  EtiquetaDeTipoDeMudanca,
  Salvo,
  Secao,
} from '@/features/changes/componentes';
import { LinkDaMudanca } from '@/features/changes/navegacao';
import {
  avancosDe,
  ehComponivel,
  motivoDeNaoCompor,
  podeCancelar,
  podeReverter,
  TERMINAIS,
} from '@/features/releases/ciclo';
import {
  EtiquetaDeSituacao,
  EtiquetaDeTipoDeRelease,
  ReguaDoCiclo,
} from '@/features/releases/componentes';
import { LinkDaListaDeEntregas } from '@/features/releases/navegacao';

import {
  assinaturaDeIcsDaRelease,
  icsDeclaradosPelasMudancas,
} from '@/features/releases/itens';
import {
  acrescentarMudanca,
  cancelarRelease,
  chavesDeRelease,
  definirIcsDaRelease,
  excluirRelease,
  moverRelease,
  obterRelease,
  retirarMudanca,
  reverterRelease,
} from '@/features/releases/releases.api';
import {
  ROTULO_DE_SITUACAO,
  type MembroDaRelease,
  type Release,
  type SituacaoDeRelease,
} from '@/features/releases/tipos';

/**
 * A ficha da versão de entrega.
 *
 * ## O que a tela existe para deixar visível
 *
 *   1. **onde a versão está no ciclo**, e para onde ela pode ir — a régua no
 *      alto, com os botões só do que existe;
 *   2. **o que ela agrupa**, na ordem de implantação;
 *   3. ★ **o que a reversão fez com cada mudança do conjunto.** Reverter uma
 *      versão é uma operação, não N reversões manuais: a versão volta atrás e
 *      **cada** mudança recebe o desfecho `revertida` com o motivo. Se isso não
 *      aparecer aqui, a operação existe no banco e não existe para quem opera.
 */
export function ReleasePage({ releaseId }: { releaseId: string }) {
  const consulta = useQuery({
    queryKey: chavesDeRelease.item(releaseId),
    queryFn: () => obterRelease(releaseId),
  });

  if (consulta.isPending) {
    return (
      <SkeletonRegion label="Carregando a versão de entrega" className="mx-auto max-w-5xl space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
      </SkeletonRegion>
    );
  }

  if (consulta.isError) {
    return (
      <div className="mx-auto max-w-5xl space-y-3">
        <AvisoDeFalha
          falha={classificarFalha(consulta.error)}
          aoRecarregar={() => void consulta.refetch()}
        />
        <LinkDaListaDeEntregas className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Versões de entrega
        </LinkDaListaDeEntregas>
      </div>
    );
  }

  const release = consulta.data;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <Cabecalho release={release} />
      <Ciclo release={release} />
      <MudancasDaRelease release={release} />
      <IcsDaRelease release={release} />
      <Planos release={release} />
    </div>
  );
}

// ── cabeçalho ──────────────────────────────────────────────────────────────

function Cabecalho({ release }: { release: Release }) {
  const pessoas = useDicionarioDePessoas([release.ownerId]);

  return (
    <header className="space-y-4">
      <LinkDaListaDeEntregas className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground">
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Versões de entrega
      </LinkDaListaDeEntregas>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm font-semibold text-gradient">{release.code}</span>
          <EtiquetaDeTipoDeRelease tipo={release.type} />
          <EtiquetaDeSituacao situacao={release.status} />
        </div>
        <h1 className="text-2xl font-semibold leading-tight tracking-tight">{release.name}</h1>
        {release.description ? (
          <p className="max-w-3xl text-sm text-muted-foreground">{release.description}</p>
        ) : null}
      </div>

      {release.status === 'ROLLED_BACK' ? (
        <div className="space-y-1 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
          <p className="flex items-center gap-2 text-sm font-semibold text-destructive">
            <Undo2 className="h-4 w-4" aria-hidden />
            Versão revertida
            {release.rolledBackAt ? ` em ${formatarDataHora(release.rolledBackAt)}` : ''}
          </p>
          <p className="text-sm text-foreground/90">{release.rollbackReason}</p>
          <p className="text-xs text-muted-foreground">
            Cada mudança do conjunto que ainda não tinha desfecho foi marcada como revertida, com
            este motivo. A que já tinha desfecho próprio não foi sobrescrita: se uma delas falhou
            sozinha e foi revertida antes, o registro dela é o que aconteceu de verdade.
          </p>
        </div>
      ) : null}

      <dl className="grid gap-x-6 gap-y-3 rounded-lg border border-border bg-card px-5 py-4 text-sm shadow-card sm:grid-cols-3">
        <Dado termo="Responsável" valor={<NomeDePessoa id={release.ownerId} pessoas={pessoas} />} />
        <Dado
          termo="Implantação prevista"
          valor={release.plannedDeployAt ? formatarDataHora(release.plannedDeployAt) : '—'}
        />
        <Dado
          termo="Implantada em"
          valor={release.actualDeployAt ? formatarDataHora(release.actualDeployAt) : '—'}
        />
        <Dado
          termo="Janela"
          valor={
            release.changeWindow ? (
              <span className="flex flex-wrap items-center gap-2">
                {release.changeWindow.name}
                <EtiquetaDeJanela situacao={release.changeWindow.status} />
                <span className="text-xs tabular-nums text-muted-foreground">
                  {rotuloDeHorario(release.changeWindow.startsAt, release.changeWindow.endsAt)}
                </span>
              </span>
            ) : (
              '—'
            )
          }
        />
        <Dado termo="Mudanças" valor={String(release.changes.length)} />
        <Dado termo="Itens de configuração" valor={String(release.cis.length)} />
      </dl>
    </header>
  );
}

// ── ciclo ──────────────────────────────────────────────────────────────────

function Ciclo({ release }: { release: Release }) {
  const cliente = useQueryClient();
  const [falha, setFalha] = useState<Falha | null>(null);
  const [revertendo, setRevertendo] = useState(false);

  const invalidar = () => {
    void cliente.invalidateQueries({ queryKey: chavesDeRelease.raiz });
    // A reversão marca cada mudança do conjunto: a ficha de cada chamado e o
    // calendário mudam junto.
    void cliente.invalidateQueries({ queryKey: chaves.raiz });
  };

  const mover = useMutation({
    mutationFn: (destino: SituacaoDeRelease) => moverRelease(release.id, destino, release.version),
    onMutate: () => setFalha(null),
    onSuccess: invalidar,
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  const cancelar = useMutation({
    mutationFn: () => cancelarRelease(release.id, release.version),
    onMutate: () => setFalha(null),
    onSuccess: invalidar,
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  const excluir = useMutation({
    mutationFn: () => excluirRelease(release.id),
    onMutate: () => setFalha(null),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chavesDeRelease.raiz });
      window.history.back();
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  const podeExcluir = release.status === 'PLANNING' || release.status === 'CANCELLED';

  const avancos = avancosDe(release.status);

  /**
   * O 409 de versão só se resolve trazendo o estado de agora.
   *
   * Sem isto o `AvisoDeFalha` de conflito prometia "Recarregar traz o estado de
   * agora" e não mostrava botão nenhum — e cada nova tentativa reenviava a
   * mesma versão obsoleta, recebendo o mesmo 409.
   */
  const recarregar = () => void cliente.refetchQueries({ queryKey: chavesDeRelease.item(release.id) });

  return (
    <Card>
      <CardHeader
        title="Ciclo"
        description="O caminho é o do ITIL e não é configurável por tenant — por isso ele é uma tabela de transições, e não um fluxo desenhável. Voltar é permitido até a versão ficar pronta: teste que reprova devolve para construção."
      />
      <CardBody className="space-y-3">
        <ReguaDoCiclo situacao={release.status} />

        <div className="flex flex-wrap gap-2">
          {avancos.map((destino) => (
            <Button
              key={destino}
              size="sm"
              variant={destino === 'DEPLOYED' ? 'primary' : 'secondary'}
              loading={mover.isPending}
              onClick={() => mover.mutate(destino)}
              icon={<ArrowRight className="h-4 w-4" aria-hidden />}
            >
              {ROTULO_DE_SITUACAO[destino]}
            </Button>
          ))}

          {podeCancelar(release.status) ? (
            <Button
              size="sm"
              variant="ghost"
              loading={cancelar.isPending}
              onClick={() => cancelar.mutate()}
            >
              Cancelar versão
            </Button>
          ) : null}

          {podeReverter(release.status) ? (
            <Button
              size="sm"
              variant="danger"
              onClick={() => setRevertendo(true)}
              icon={<Undo2 className="h-4 w-4" aria-hidden />}
            >
              Reverter a versão inteira
            </Button>
          ) : null}

          {podeExcluir ? (
            <Button
              size="sm"
              variant="danger"
              loading={excluir.isPending}
              onClick={() => excluir.mutate()}
            >
              Excluir versão
            </Button>
          ) : null}

          {avancos.length === 0 && !podeReverter(release.status) && !podeCancelar(release.status) && !podeExcluir ? (
            <p className="text-sm text-muted-foreground">
              Situação final: desta a versão não sai mais.
            </p>
          ) : null}
        </div>

        {release.status === 'READY' && release.changes.length === 0 ? (
          <p className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
            A versão não agrupa nenhuma mudança. O servidor recusa iniciar a implantação de um
            conjunto vazio — implantar nada não é implantar.
          </p>
        ) : null}

        {falha ? <AvisoDeFalha falha={falha} aoRecarregar={recarregar} /> : null}
      </CardBody>

      {revertendo ? (
        <DialogoDeReversao release={release} aoFechar={() => setRevertendo(false)} />
      ) : null}
    </Card>
  );
}

const MINIMO_DO_MOTIVO = 15;

function DialogoDeReversao({ release, aoFechar }: { release: Release; aoFechar: () => void }) {
  const cliente = useQueryClient();
  const [motivo, setMotivo] = useState('');
  const [falha, setFalha] = useState<Falha | null>(null);
  const [resultado, setResultado] = useState<number | null>(null);

  const reverter = useMutation({
    mutationFn: () => reverterRelease(release.id, motivo.trim(), release.version),
    onMutate: () => setFalha(null),
    onSuccess: (resposta) => {
      setResultado(resposta.changesMarked.length);
      void cliente.invalidateQueries({ queryKey: chavesDeRelease.raiz });
      void cliente.invalidateQueries({ queryKey: chaves.raiz });
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  const curto = motivo.trim().length < MINIMO_DO_MOTIVO;

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={`Reverter ${release.code}`}
      descricao={`A versão volta atrás e cada uma das ${release.changes.length} mudança(s) do conjunto é marcada como revertida, numa transação só.`}
    >
      <form
        className="space-y-4 px-5 py-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (!curto && resultado === null) reverter.mutate();
        }}
      >
        {resultado === null ? (
          <>
            <Campo
              rotulo="Motivo da reversão"
              htmlFor="reversao-motivo"
              dica={`Pelo menos ${MINIMO_DO_MOTIVO} caracteres — o banco também exige. Sem mínimo, o motivo seria “erro”, e “por que revertemos a ${release.code}?” continuaria sem resposta na reunião seguinte.`}
            >
              <textarea
                id="reversao-motivo"
                rows={4}
                value={motivo}
                onChange={(evento) => setMotivo(evento.target.value)}
                className={CLASSE_DE_CAMPO}
              />
            </Campo>

            <ul className="space-y-1 rounded-md border border-border px-3 py-2 text-xs">
              <li className="font-semibold">Serão marcadas:</li>
              {release.changes.map((membro) => (
                <li key={membro.ticketId} className="flex items-center gap-2">
                  <span className="font-mono">{membro.ticket.number}</span>
                  <span className="min-w-0 flex-1 truncate">{membro.ticket.subject}</span>
                  {membro.ticket.changeDetail?.outcome ? (
                    <span className="text-muted-foreground">
                      já tem desfecho — não será sobrescrita
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>

            {falha ? (
              <AvisoDeFalha
                falha={falha}
                aoRecarregar={() =>
                  void cliente.refetchQueries({ queryKey: chavesDeRelease.item(release.id) })
                }
              />
            ) : null}

            <div className="flex justify-end gap-2 border-t border-border pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={aoFechar}
                disabled={reverter.isPending}
              >
                Cancelar
              </Button>
              <Button type="submit" variant="danger" loading={reverter.isPending} disabled={curto}>
                Reverter
              </Button>
            </div>
          </>
        ) : (
          <div className="space-y-4">
            <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              Versão revertida. {resultado} mudança(s) marcada(s) como revertidas com este motivo.
              {release.changes.length - resultado > 0
                ? ` ${release.changes.length - resultado} não foi(ram) tocada(s) por já ter(em) desfecho próprio.`
                : ''}
            </p>
            <div className="flex justify-end">
              <Button onClick={aoFechar}>Fechar</Button>
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
}

// ── composição ─────────────────────────────────────────────────────────────

function MudancasDaRelease({ release }: { release: Release }) {
  const [acrescentando, setAcrescentando] = useState(false);
  const componivel = ehComponivel(release.status);
  const motivo = motivoDeNaoCompor(release.status);

  return (
    <Card>
      <CardHeader
        title="Mudanças agrupadas"
        description="Na ordem de implantação. Uma mudança pertence a uma versão só — do contrário, reverter uma delas deixaria a outra afirmando que ela subiu."
        action={
          componivel ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setAcrescentando(true)}
              icon={<Plus className="h-4 w-4" aria-hidden />}
            >
              Acrescentar mudança
            </Button>
          ) : undefined
        }
      />
      <CardBody className="space-y-3">
        {motivo ? <p className="text-xs text-muted-foreground">{motivo}</p> : null}

        {release.changes.length === 0 ? (
          <EmptyState
            title="Nenhuma mudança agrupada"
            description="Uma versão vazia não implanta: o servidor recusa iniciar a implantação de um conjunto vazio."
          />
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border">
            {release.changes.map((membro) => (
              <LinhaDaMudanca
                key={membro.ticketId}
                membro={membro}
                releaseId={release.id}
                componivel={componivel}
              />
            ))}
          </ul>
        )}
      </CardBody>

      {acrescentando ? (
        <DialogoDeMudanca release={release} aoFechar={() => setAcrescentando(false)} />
      ) : null}
    </Card>
  );
}

function LinhaDaMudanca({
  membro,
  releaseId,
  componivel,
}: {
  membro: MembroDaRelease;
  releaseId: string;
  componivel: boolean;
}) {
  const cliente = useQueryClient();
  const [falha, setFalha] = useState<Falha | null>(null);

  const retirar = useMutation({
    mutationFn: () => retirarMudanca(releaseId, membro.ticketId),
    onMutate: () => setFalha(null),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chavesDeRelease.raiz }),
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  const detalhe = membro.ticket.changeDetail;
  const revertida = detalhe?.outcome === 'ROLLED_BACK';

  return (
    <li className={cn('space-y-1 px-3 py-2', revertida && 'bg-destructive/5')}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="w-6 shrink-0 text-right font-mono text-xs tabular-nums text-muted-foreground">
          {membro.sequence}
        </span>
        <LinkDaMudanca
          ticketId={membro.ticketId}
          className="font-mono text-xs font-semibold text-primary hover:underline"
        >
          {membro.ticket.number}
        </LinkDaMudanca>
        <span className="min-w-0 flex-1 truncate">{membro.ticket.subject}</span>
        {detalhe ? <EtiquetaDeTipoDeMudanca tipo={detalhe.changeType} /> : null}
        {detalhe ? <EtiquetaDeRisco nivel={detalhe.riskLevel} /> : null}
        {detalhe?.outcome ? <EtiquetaDeDesfecho desfecho={detalhe.outcome} /> : null}
        {componivel ? (
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Retirar ${membro.ticket.number} da versão`}
            loading={retirar.isPending}
            onClick={() => retirar.mutate()}
            icon={<Trash2 className="h-4 w-4" aria-hidden />}
          />
        ) : null}
      </div>
      {falha ? (
        <AvisoDeFalha
          falha={falha}
          aoRecarregar={() => void cliente.refetchQueries({ queryKey: chavesDeRelease.raiz })}
        />
      ) : null}
    </li>
  );
}

/** Escolher a mudança que entra — só chamados do tipo mudança são aceitos. */
function DialogoDeMudanca({ release, aoFechar }: { release: Release; aoFechar: () => void }) {
  const cliente = useQueryClient();
  const [escolhido, setEscolhido] = useState('');
  const [ordem, setOrdem] = useState(String(release.changes.length));
  const [falha, setFalha] = useState<Falha | null>(null);

  const candidatos = useQuery({
    queryKey: ['chamados', 'lista', 'mudancas-para-entrega'],
    queryFn: () => listarChamados({ type: ['CHANGE'], openOnly: true, limit: 100 }),
  });

  const acrescentar = useMutation({
    mutationFn: () => acrescentarMudanca(release.id, escolhido, Number.parseInt(ordem, 10) || 0),
    onMutate: () => setFalha(null),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chavesDeRelease.raiz });
      aoFechar();
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  const jaDentro = new Set(release.changes.map((membro) => membro.ticketId));
  const disponiveis = (candidatos.data?.items ?? []).filter((chamado) => !jaDentro.has(chamado.id));

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={`Acrescentar mudança a ${release.code}`}
      descricao="A lista traz os chamados do tipo mudança que ainda estão abertos. O banco recusa qualquer outro tipo — a checagem do servidor existe só para a mensagem ser em português."
    >
      <form
        className="space-y-4 px-5 py-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (escolhido !== '') acrescentar.mutate();
        }}
      >
        {candidatos.isPending ? <Skeleton className="h-9 w-full" /> : null}
        {candidatos.isError ? (
          <AvisoDeFalha
            falha={classificarFalha(candidatos.error)}
            aoRecarregar={() => void candidatos.refetch()}
          />
        ) : null}

        <Campo rotulo="Mudança" htmlFor="entrega-mudanca">
          <select
            id="entrega-mudanca"
            value={escolhido}
            onChange={(evento) => setEscolhido(evento.target.value)}
            className={CLASSE_DE_CAMPO}
          >
            <option value="">Escolha uma mudança…</option>
            {disponiveis.map((chamado) => (
              <option key={chamado.id} value={chamado.id}>
                {chamado.number} — {chamado.subject}
              </option>
            ))}
          </select>
        </Campo>

        {disponiveis.length === 0 && !candidatos.isPending ? (
          <p className="text-xs text-muted-foreground">
            Nenhuma mudança aberta fora desta versão. Uma mudança já pertencente a outra versão não
            aparece aqui — e o servidor a recusaria de qualquer forma.
          </p>
        ) : null}

        <Campo
          rotulo="Ordem de implantação"
          htmlFor="entrega-ordem"
          dica="Empate resolve pela data em que a mudança entrou na versão."
        >
          <input
            id="entrega-ordem"
            type="number"
            min={0}
            value={ordem}
            onChange={(evento) => setOrdem(evento.target.value)}
            className={cn(CLASSE_DE_CAMPO, 'w-24')}
          />
        </Campo>

        {falha ? (
          <AvisoDeFalha
            falha={falha}
            aoRecarregar={() => void cliente.refetchQueries({ queryKey: chavesDeRelease.raiz })}
          />
        ) : null}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={aoFechar}
            disabled={acrescentar.isPending}
          >
            Cancelar
          </Button>
          <Button type="submit" loading={acrescentar.isPending} disabled={escolhido === ''}>
            Acrescentar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ── itens de configuração ──────────────────────────────────────────────────

/** Uma linha do formulário de itens movidos: o texto do campo, ainda cru. */
interface LinhaDeIc {
  ciId: string;
  versionBefore: string;
  versionAfter: string;
}

/**
 * De qual versão para qual — e agora dá para preencher.
 *
 * A lista é substituída inteira (`POST /releases/:id/cis`), e os itens
 * oferecidos são os que as mudanças do conjunto declaram encostar: é o caminho
 * que faz sentido e o único que a API permite montar sem uma listagem do CMDB.
 * Antes esta seção era só leitura, com um estado vazio que cobrava a lista sem
 * oferecer onde escrevê-la — a rota existia e nenhum botão a chamava.
 *
 * Uma consulta por mudança do conjunto: `GET /tickets/:id/change` é o único
 * lugar onde os itens afetados aparecem com nome e tipo. São poucas por versão
 * de entrega, e elas correm em paralelo.
 *
 * Depois que a versão vira histórico — revertida ou cancelada — a lista fecha
 * junto: "o que exatamente subiu?" não se reescreve depois do fato.
 */
function IcsDaRelease({ release }: { release: Release }) {
  const cliente = useQueryClient();
  const [falha, setFalha] = useState<Falha | null>(null);
  const editavel = !TERMINAIS.has(release.status);

  const mudancas = useQueries({
    queries: release.changes.map((membro) => ({
      queryKey: chavesDeMudanca.mudanca(membro.ticketId),
      queryFn: () => obterMudanca(membro.ticketId),
      staleTime: 60_000,
    })),
  });

  const candidatos = icsDeclaradosPelasMudancas(
    mudancas.map((consulta) => consulta.data?.affectedCis),
    release.cis,
  );

  const rascunho = useRascunho<LinhaDeIc[]>(
    () =>
      release.cis.map((linha) => ({
        ciId: linha.ciId,
        versionBefore: linha.versionBefore ?? '',
        versionAfter: linha.versionAfter ?? '',
      })),
    assinaturaDeIcsDaRelease(release.cis),
  );
  const linhas = rascunho.valor;

  const gravar = useMutation({
    mutationFn: () =>
      definirIcsDaRelease(
        release.id,
        linhas.map((linha) => ({
          ciId: linha.ciId,
          versionBefore: linha.versionBefore.trim() === '' ? null : linha.versionBefore.trim(),
          versionAfter: linha.versionAfter.trim() === '' ? null : linha.versionAfter.trim(),
        })),
      ),
    onMutate: () => setFalha(null),
    onSuccess: () => {
      // A resposta vem sem o item de configuração associado: quem desenha o
      // nome é a ficha, então ela recarrega em vez de a tela costurar.
      rascunho.confirmar();
      void cliente.invalidateQueries({ queryKey: chavesDeRelease.raiz });
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  const alterar = (valor: (atual: LinhaDeIc[]) => LinhaDeIc[]) => {
    gravar.reset();
    rascunho.definir(valor);
  };

  const escolhido = (ciId: string) => linhas.find((linha) => linha.ciId === ciId);

  return (
    <Card>
      <CardHeader
        title="Itens de configuração movidos"
        description="De qual versão para qual. É o registro que responde “o que exatamente subiu?” depois que a entrega acabou."
        action={
          editavel ? (
            <div className="flex items-center gap-2">
              <Salvo visivel={gravar.isSuccess} />
              <Button
                size="sm"
                variant="secondary"
                loading={gravar.isPending}
                disabled={rascunho.desatualizado}
                onClick={() => gravar.mutate()}
                icon={<Save className="h-4 w-4" aria-hidden />}
              >
                Salvar itens
              </Button>
            </div>
          ) : undefined
        }
      />
      <CardBody className="space-y-3">
        {!editavel ? (
          <p className="text-xs text-muted-foreground">
            A versão está em “{ROTULO_DE_SITUACAO[release.status].toLowerCase()}”, que é situação
            final: esta lista é histórico e não se reescreve.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            A lista oferece os itens que <strong>as mudanças desta versão declaram encostar</strong>
            . Não há rota que liste o inventário inteiro — um item que nenhuma mudança declarou não
            aparece aqui, e o caminho é declará-lo na ficha da mudança.
          </p>
        )}

        {mudancas.some((consulta) => consulta.isPending) ? (
          <Skeleton className="h-20 w-full" />
        ) : null}

        {candidatos.length === 0 ? (
          <EmptyState
            title="Nenhum item de configuração para declarar"
            description="Nenhuma das mudanças agrupadas declara item de configuração afetado. O caminho começa lá: sem item declarado na mudança, a versão diz o que agrupou mas não tem como dizer o que moveu."
          />
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border">
            {candidatos.map((ci) => {
              const linha = escolhido(ci.id);
              return (
                <li key={ci.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
                  <label className="flex min-w-0 flex-1 items-center gap-2">
                    <input
                      type="checkbox"
                      disabled={!editavel}
                      checked={linha !== undefined}
                      onChange={() =>
                        alterar((atual) =>
                          atual.some((item) => item.ciId === ci.id)
                            ? atual.filter((item) => item.ciId !== ci.id)
                            : [...atual, { ciId: ci.id, versionBefore: '', versionAfter: '' }],
                        )
                      }
                    />
                    <span className="truncate font-medium">{ci.name}</span>
                    <span className="text-xs text-muted-foreground">{ci.ciType}</span>
                  </label>

                  {linha ? (
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1.5 text-xs">
                        <span className="sr-only">Versão de {ci.name} antes da entrega</span>
                        <input
                          value={linha.versionBefore}
                          disabled={!editavel}
                          placeholder="de"
                          onChange={(evento) => {
                            const texto = evento.target.value;
                            alterar((atual) =>
                              atual.map((item) =>
                                item.ciId === ci.id ? { ...item, versionBefore: texto } : item,
                              ),
                            );
                          }}
                          className={cn(CLASSE_DE_CAMPO, 'w-24 px-2 py-1')}
                        />
                      </label>
                      <span aria-hidden className="text-muted-foreground">
                        →
                      </span>
                      <label className="flex items-center gap-1.5 text-xs">
                        <span className="sr-only">Versão de {ci.name} depois da entrega</span>
                        <input
                          value={linha.versionAfter}
                          disabled={!editavel}
                          placeholder="para"
                          onChange={(evento) => {
                            const texto = evento.target.value;
                            alterar((atual) =>
                              atual.map((item) =>
                                item.ciId === ci.id ? { ...item, versionAfter: texto } : item,
                              ),
                            );
                          }}
                          className={cn(CLASSE_DE_CAMPO, 'w-24 px-2 py-1')}
                        />
                      </label>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}

        {rascunho.desatualizado ? <AvisoDeRegistroMudado aoDescartar={rascunho.redefinir} /> : null}

        {falha ? (
          <AvisoDeFalha
            falha={falha}
            aoRecarregar={() => {
              rascunho.redefinir();
              void cliente.refetchQueries({ queryKey: chavesDeRelease.item(release.id) });
            }}
          />
        ) : null}
      </CardBody>
    </Card>
  );
}

// ── planos ─────────────────────────────────────────────────────────────────

function Planos({ release }: { release: Release }) {
  const planos = [
    { titulo: 'Plano de implantação', texto: release.deploymentPlan },
    { titulo: 'Plano de verificação', texto: release.verificationPlan },
    { titulo: 'Plano de retorno', texto: release.rollbackPlan },
  ];

  return (
    <Card>
      <CardHeader
        title="Planos"
        description="Definidos na criação. Não há rota de alteração para versão de entrega — o que está aqui é o que foi registrado quando ela nasceu."
      />
      <CardBody className="space-y-4">
        {planos.map((plano) => (
          <Secao key={plano.titulo} titulo={plano.titulo}>
            {plano.texto ? (
              <p className="whitespace-pre-wrap text-sm leading-relaxed">{plano.texto}</p>
            ) : (
              <p className="text-sm italic text-muted-foreground">
                não registrado na criação, e sem rota para acrescentar depois
              </p>
            )}
          </Secao>
        ))}
      </CardBody>
    </Card>
  );
}
